import React, { useMemo, useRef, useState, useLayoutEffect } from "react";
import cx from "classnames";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';

import Thumbnail from "src/components/thumbnail";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import { Stream } from "src/types/streams";

import styles from "./selectedContentCard.module.scss";
import { Asset } from "src/types/assets";
import { Collection } from "src/types/collections";

import _ from "lodash";

interface EntityCardProps {
  entity: Stream | Asset | Collection;
  // eslint-disable-next-line no-unused-vars
  onRemove?: (entity: Stream | Asset | Collection) => void;
  activeDragEntityId?: string | number
}

const EntityCard: React.FC<EntityCardProps> = ({ entity, onRemove, activeDragEntityId }) => {

  const textRef = useRef<HTMLDivElement | null>(null);

  const [isTruncated, setIsTruncated] = useState(false);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: _.get(entity, "id", "") });

  const handleRemove = () => {
    if (onRemove) {
      onRemove(entity);
    }
  };

  const entityType = entity?.entityType;

  const getWidth = useMemo(() => {
    switch (entityType) {
      case "stream":
        return 100;
      case "collection":
        return 150;
      default:
        return 100;
    }
  }, [entityType]);


  const transformStyles = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const banner = _.get(entity, "banner.background") || _.get(entity, "icon");
  
  useLayoutEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        setIsTruncated(textRef.current.scrollWidth > textRef.current.offsetWidth);
      }
    };

    const observer = new ResizeObserver(checkTruncation);
    if (textRef.current) observer.observe(textRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={cx(styles.card,{
      [styles.dragging] : activeDragEntityId === entity?.id
    })} >
      <button className={styles.remove} onClick={handleRemove}>
        <i className="fa-regular fa-circle-xmark"></i>
      </button>
      <div ref={setNodeRef} style={transformStyles} {...attributes} {...listeners} className={cx(styles.icon, {
        [styles.stream]: entityType === "stream",
        [styles.collection]: entityType === "collection",
      })}>
        <Thumbnail
          media={banner}
          defaultImage="/images/assets/default_asset.jpg"
        />
        <div className={styles.overlay}></div>

      </div>
      <div className={styles.cardContent}>
        <div className={styles.name}>
          <TruncateWithTooltip 
            text={entity?.name || ""}
            placement="bottom"
            style={{ maxWidth: getWidth }}
            />
        </div>
      </div>
    </div>
  );
};

export { EntityCard };
